import React, { useEffect, useState, useCallback } from "react";
import { Box, Paper, Alert, IconButton, Tooltip, useTheme, useMediaQuery } from "@mui/material";
import { Refresh as RefreshIcon, Menu as MenuIcon } from "@mui/icons-material";
import ChatHeader from "./ChatHeader";
import MessagesList from "./MessagesList";
import MessageInput from "./MessageInput";
import TypingIndicator from "./TypingIndicator";
import ChatSidebar from "./ChatSidebar";
import { useChatState, useChatSessions } from '../hooks';

const ChatContainer = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

    // Initialize sessions hook with auto-refresh enabled
    const {
        silentRefresh: refreshSessions
    } = useChatSessions({
        autoRefresh: true,           // Enable auto-refresh every 30 seconds
        refreshInterval: 30000,      // 30 seconds
        refreshOnFocus: true         // Refresh when window gets focus
    });

    // Handle session updates (when new messages are sent)
    const handleSessionUpdate = useCallback((sessionId, lastMessage) => {
        // Trigger silent refresh of sessions list after new message
        refreshSessions();
    }, [refreshSessions]);

    const {
        // State
        messages,
        currentSession,
        isLoading,
        isTyping,
        error,
        isInitialized,

        // Actions
        initializeChat,
        sendMessage,
        startNewSession,
        switchToSession,
        retryLastMessage,

        // Computed values
        canSendMessage,
    } = useChatState({ onSessionUpdate: handleSessionUpdate });

    // Initialize chat when component mounts
    useEffect(() => {
        initializeChat();
    }, [initializeChat]);

    // Handle mobile responsiveness
    useEffect(() => {
        setSidebarOpen(!isMobile);
    }, [isMobile]);

    const handleSendMessage = async (message) => {
        await sendMessage(message);
    };

    const handleStartNewSession = () => {
        startNewSession();
        if (isMobile) {
            setSidebarOpen(false); // Close sidebar on mobile after creating new session
        }
    };

    const handleSessionSelect = async (sessionId) => {
        await switchToSession(sessionId);
        if (isMobile) {
            setSidebarOpen(false); // Close sidebar on mobile after selecting session
        }
    };

    const handleRetry = () => {
        retryLastMessage();
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    return (
        <Box sx={{ 
            height: "100%", 
            display: "flex", 
            overflow: "hidden"
        }}>
            {/* Sidebar with Sessions */}
            <ChatSidebar
                open={sidebarOpen}
                onToggle={toggleSidebar}
                onSessionSelect={handleSessionSelect}
                currentSessionId={currentSession?.sessionId}
                onNewSession={handleStartNewSession}
            />

            {/* Main Chat Area */}
            <Box sx={{ 
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
            }}>
                <Paper 
                    elevation={0} 
                    sx={{ 
                        height: "100%", 
                        display: "flex", 
                        flexDirection: "column",
                        overflow: "hidden",
                        borderRadius: 0,
                        bgcolor: "background.default"
                    }}
                >
                    <ChatHeader 
                        session={currentSession} 
                        onNewSession={handleStartNewSession}
                        onToggleSidebar={toggleSidebar}
                        showSidebarToggle={isMobile}
                    />
                    
                    <Box sx={{ 
                        flex: 1, 
                        display: "flex", 
                        flexDirection: "column",
                        overflow: "hidden"
                    }}>
                        {/* Error Display */}
                        {error && (
                            <Alert 
                                severity="error" 
                                sx={{ m: 1, mb: 0 }}
                                action={
                                    <Tooltip title="Retry last message">
                                        <IconButton
                                            size="small"
                                            onClick={handleRetry}
                                            color="inherit"
                                        >
                                            <RefreshIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                }
                            >
                                {error}
                            </Alert>
                        )}

                        {/* Messages List */}
                        <MessagesList 
                            messages={messages} 
                            isLoading={isLoading && !isInitialized}
                        />
                        
                        {/* Typing Indicator */}
                        {isTyping && <TypingIndicator />}
                    </Box>
                    
                    {/* Message Input */}
                    <MessageInput 
                        onSendMessage={handleSendMessage}
                        disabled={!canSendMessage}
                        placeholder={
                            !isInitialized 
                                ? "Initializing chat..." 
                                : "Type your message... (Enter to send, Shift+Enter for new line)"
                        }
                    />
                </Paper>
            </Box>
        </Box>
    );
};

export default ChatContainer; 